import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import {
  searchHospitals,
  hospitalsQueryKey,
  type HospitalListItem,
} from '../api/hospitals'
import {
  fetchSpecialists,
  specialistsKey,
  type SpecialistSearchResult,
} from '../api/specialists'
import { Button } from '../components/Button'
import { CountChip } from '../components/CountChip'
import { EmptyState } from '../components/EmptyState'
import { HospitalCard } from '../components/HospitalCard'
import { Icon } from '../components/Icon'
import { MapPlaceholder } from '../components/MapPlaceholder'
import {
  PAGE_GUTTER_DESKTOP,
  PageContent,
  PageShell,
} from '../components/PageShell'
import { SearchInput } from '../components/SearchInput'
import { SpecialistRow } from '../components/SpecialistRow'
import { SearchReading } from '../components/search/SearchReading'
import {
  answeringSpecialty,
  searchDestination,
} from '../components/search/destination'
import { ROW1, ROW2, row2State, type ChipSelection } from '../components/chips'
import { BED_TYPE_DEFS } from '../lib/bedTypes'
import { bedBadges, matchesSearch, serviceTags } from '../lib/hospitalCard'
import { useGeolocation, type GeoStatus } from '../lib/useGeolocation'

// The chip vocabulary/logic (chips.ts, row2State) is read-only here — used to
// validate + clamp what arrives in the URL. It is never redefined (ADR-0024/0012).
const VALID_ROW1 = new Set(ROW1.map((c) => c.id))
const VALID_ROW2 = new Set(ROW2.map((c) => c.id))

/** The intents that are a patient describing what is wrong, or asking for a
 * doctor -- the searches the owner ruled should lead with doctors (M33-T04b,
 * 2026-10-14). A browse, a hospital search, a treatment or a test leads with
 * the hospitals, as before. */
const DOCTOR_INTENTS: ReadonlySet<string> = new Set([
  'symptom',
  'condition',
  'find_doctor',
])

/** The profile, told why the patient is there (M33-T04). Without the specialty
 * the profile cannot open the right tab, filter the right doctors or offer the
 * right booking — everything downstream has to guess. */
function hospitalPath(slug: string, specialty: string | null): string {
  return specialty === null
    ? `/hospital/${slug}`
    : `/hospital/${slug}?specialty=${encodeURIComponent(specialty)}`
}

/** Parse a chip selection from the URL (`row1` repeated, single `row2`),
 * dropping unknown ids and a Row-2 value the matrix disables. */
function selectionFromParams(params: URLSearchParams): ChipSelection {
  const row1 = params.getAll('row1').filter((id) => VALID_ROW1.has(id))
  const raw = params.get('row2')
  const row2 =
    raw && VALID_ROW2.has(raw) && row2State(row1).enabled[raw] ? raw : null
  return { row1, row2 }
}

function paramsFromSelection(
  selection: ChipSelection,
  q: string,
): URLSearchParams {
  const p = new URLSearchParams()
  for (const id of selection.row1) p.append('row1', id)
  if (selection.row2) p.set('row2', selection.row2)
  if (q.trim()) p.set('q', q.trim())
  return p
}

/** What the list is filtered by, shown where the chip rows used to be
 * (ADR-0036). The chip *control* is retired — every filter now arrives from an
 * upstream workflow (the bed-type picker, a tile deep link) — so this makes the
 * active filter visible and reversible instead of leaving the count unexplained.
 * Renders nothing when only a text search is active: the search box already
 * shows the term. */
function ActiveFilter({
  selection,
  bedTypeKey,
  onClear,
  onChangeBedType,
}: {
  selection: ChipSelection
  bedTypeKey: string | null
  onClear: () => void
  onChangeBedType: () => void
}): ReactNode {
  const bedType = BED_TYPE_DEFS.find((d) => d.key === bedTypeKey) ?? null
  const labels = selection.row1.map(
    (id) => ROW1.find((c) => c.id === id)?.label ?? id,
  )
  if (!bedType && labels.length === 0) return null

  const isBedFlow = bedType !== null
  return (
    <div
      role="group"
      aria-label="Active filter"
      className="flex items-center gap-2 py-3"
    >
      <span className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
        Showing
      </span>
      {/* M17-T08 — the frames draw an interactive filter chip panel here.
          ADR-0036 retired that control and kept only its vocabulary, so this is
          a READOUT: `readOnly` renders a span, not a button. It states what the
          list is scoped to; the single action beside it changes the scope by
          leaving for the picker, or clears it. */}
      <CountChip
        readOnly
        selected
        label={isBedFlow ? bedType.label : labels.join(' · ')}
      />
      <button
        type="button"
        onClick={isBedFlow ? onChangeBedType : onClear}
        className="ml-auto rounded-r2 px-2 py-1 text-[12px] font-medium text-accent-ink hover:bg-accent-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {isBedFlow ? 'Change bed type' : 'Clear'}
      </button>
    </div>
  )
}

/**
 * Hospital results (M12-T01, ADR-0026; chips retired by ADR-0036) — the hospital
 * list. The filter still lives in the URL (`row1`/`row2`) so tiles and the
 * bed-type picker deep-link with it pre-applied and links stay shareable
 * (`/?row1=icu&bedtype=icu`); only the on-screen chip control is gone, replaced
 * by `ActiveFilter`. Text search filters client-side (seeded once from `?q=`).
 * Loading/error/empty are explicit (CLAUDE.md).
 */
export function HospitalResults(): ReactNode {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const geo = useGeolocation()

  const selection = useMemo(() => selectionFromParams(params), [params])
  const [search, setSearch] = useState(() => params.get('q') ?? '')

  // The SERVER query keys off the URL, not the live input. Typing must not fire
  // a request per keystroke, and the hero submits by navigating here with `?q=`
  // (M22-T09).
  const submitted = params.get('q') ?? ''

  const query = useQuery({
    queryKey: hospitalsQueryKey(selection, geo.coords, submitted),
    queryFn: ({ signal }) =>
      searchHospitals(selection, geo.coords, signal, submitted),
  })
  const [mapView, setMapView] = useState(false)

  // Understood, and nothing to list for it: a specialty nobody staffs, or a
  // procedure nobody offers (M32-T05). The reading block says the AMTC team
  // arranges it, so the empty state must not also tell the patient to clear a
  // filter or widen the search — advice to rephrase a query we understood on
  // the first try, directly under the line that says so. The specialist page
  // has had this guard since M22; this page did not.
  const interpretation = query.data?.interpretation ?? null
  const carried =
    interpretation === null ? null : answeringSpecialty(interpretation)
  const procedures = interpretation?.procedures ?? []

  // A SYMPTOM SEARCH LEADS WITH DOCTORS (M33-T04b, owner 2026-10-14): "if
  // symptom search the hospitals page should show doctors first; if general
  // browse, show the hospitals." A patient who typed a symptom wants a doctor
  // who handles it, not a building that employs one.
  //
  // ONLY A SYMPTOM-SHAPED SEARCH, ANSWERED BY A STAFFED SPECIALIST:
  // - `route === 'search'` alone is not enough. The resolver also says
  //   `search` for `eye clinic` (intent hospital) and `cardiology` (a browse),
  //   and the owner's ruling sends a browse to the hospitals.
  // - `specialists` only. At `generalist` the panel names the generalist and
  //   links to their doctors (M33-T11), and the hospitals beneath are theirs;
  //   a doctor list here too would be a third block. At `concierge` there is
  //   no list at all.
  // - Not while the patient is narrowing the hospitals by typing: the doctors
  //   are not narrowed with them, and the two would disagree.
  // A browse, a chip filter and a hospital name carry no reading; a procedure
  // is reached through the hospitals that offer it. One specialty (M33-T04c).
  const refining = search.trim() !== submitted.trim()
  const doctorsFor =
    interpretation !== null &&
    interpretation.route === 'search' &&
    DOCTOR_INTENTS.has(interpretation.intent) &&
    searchDestination(interpretation) === 'specialists' &&
    !refining
      ? carried
      : null
  const doctors = useQuery({
    queryKey: specialistsKey(doctorsFor, ''),
    queryFn: ({ signal }) => fetchSpecialists(doctorsFor, '', signal),
    enabled: doctorsFor !== null,
  })

  // Where this search ends, by the same rule the panel and the server use
  // (M33-T11): the page gates its own calls to action on it, as `/specialists`
  // does, rather than on a heuristic of its own.
  const destination =
    interpretation === null ? null : searchDestination(interpretation)
  const understoodButUnavailable =
    interpretation !== null &&
    (interpretation.route === 'procedure'
      ? procedures.length > 0 && procedures.every((p) => p.offer_count === 0)
      : interpretation.understood_as.length > 0 &&
        interpretation.understood_as.every((sp) => !sp.offered))

  // When the bed-type picker deep-links here (`?bedtype=icu`, M12-T02), surface
  // the hospitals with the most of that type free first. Stable sort keeps the
  // server order (distance/relevance) as the tiebreak. The chip already narrowed
  // the list; `bedtype` only re-orders. Unknown values are ignored.
  const bedTypeFreeKey = useMemo(() => {
    const key = params.get('bedtype')
    return BED_TYPE_DEFS.find((d) => d.key === key)?.free ?? null
  }, [params])

  const filtered = useMemo(() => {
    // Client-side text filter applies only to what the user has typed BEYOND
    // what the server already answered. Re-filtering the server's own results
    // would throw away every semantic match: the hospitals returned for
    // "skin doctor" have no such words in their name, city or address.
    const refinement = search.trim() === submitted.trim() ? '' : search
    const list = (query.data?.data ?? []).filter((h) =>
      matchesSearch(h, refinement),
    )
    if (bedTypeFreeKey === null) return list
    return [...list].sort(
      (a, b) => b.beds[bedTypeFreeKey] - a.beds[bedTypeFreeKey],
    )
  }, [query.data, search, submitted, bedTypeFreeKey])

  const clearFilter = () =>
    setParams(paramsFromSelection({ row1: [], row2: null }, search))

  return (
    <PageShell measure="full">
      <header
        className={`flex items-center gap-2 border-b border-line-1 px-4 py-3 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back to home"
          onClick={() => navigate('/')}
          className="rounded-full p-1 text-ink-3 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={20} />
        </button>
        <h1 className="text-base font-bold tracking-tight text-ink-1">
          Hospitals
        </h1>
      </header>

      <PageContent>
        <div className="flex items-stretch gap-2 pt-3">
          {/* SUBMITTING RE-RUNS THE SEARCH (M33-T11). The panel can ask the
              patient to clarify a query; typing used to filter the empty list
              in the browser only, so clarifying did nothing. Enter now sends
              the new words to the server, and clearing the box drops the old
              query -- the reading must not outlive the search. */}
          <form
            role="search"
            className="flex flex-1"
            onSubmit={(e) => {
              e.preventDefault()
              const next = new URLSearchParams(params)
              if (search.trim()) next.set('q', search.trim())
              else next.delete('q')
              setParams(next)
            }}
          >
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value)
                if (value.trim() === '' && submitted !== '') {
                  const next = new URLSearchParams(params)
                  next.delete('q')
                  setParams(next)
                }
              }}
              label="Search hospitals"
              placeholder="Search hospitals, areas"
              className="flex-1"
            />
          </form>
          <NearMeButton
            status={geo.status}
            located={geo.coords != null}
            onClick={geo.request}
          />
        </div>
        <GeoNote status={geo.status} />

        <ActiveFilter
          selection={selection}
          bedTypeKey={params.get('bedtype')}
          onClear={clearFilter}
          onChangeBedType={() => navigate('/beds')}
        />

        {query.data?.interpretation && (
          <SearchReading
            interpretation={query.data.interpretation}
            subject="hospitals"
            onEmergency={() => navigate('/emergency')}
            onConcierge={() => navigate('/specialists')}
            // THE GENERALIST, NOT AMTC (M33-T11). Without this the panel said
            // "the AMTC team arranges this" above a list of the very hospitals
            // whose generalists answer it -- two answers on one screen.
            onGeneralist={(slug) =>
              navigate(`/specialists?specialty=${encodeURIComponent(slug)}`)
            }
          />
        )}

        {doctorsFor !== null && (
          <DoctorsFirst
            query={doctors}
            specialty={doctorsFor}
            onOpen={(slug) => navigate(`/specialists/${slug}`)}
          />
        )}

        <section
          {...(doctorsFor !== null
            ? { 'aria-labelledby': 'results-hospitals' }
            : {})}
          className="flex flex-1 flex-col"
        >
          {doctorsFor !== null && (
            <h2
              id="results-hospitals"
              className="pb-1.5 text-sm font-semibold text-ink-1"
            >
              Hospitals
            </h2>
          )}
          <div className="flex items-center justify-between pb-1.5">
            <ResultCount query={query} count={filtered.length} />
            <ViewSwitch mapView={mapView} onChange={setMapView} />
          </div>

          <div className="flex flex-1 flex-col pb-4">
            {query.isPending ? (
              <ListSkeleton />
            ) : query.isError ? (
              <ErrorState onRetry={() => void query.refetch()} />
            ) : filtered.length === 0 &&
              (destination === 'emergency' ||
                interpretation?.route === 'no_match') ? (
              // ONE ANSWER (M33-T11). An emergency: the alert above is the whole
              // page. A query we could not read: the panel asks the patient to
              // clarify or call AMTC. Neither gets "clear your filters" too.
              null
            ) : filtered.length === 0 &&
              (understoodButUnavailable || destination === 'concierge') ? (
              <EmptyState
                title="No hospitals to list yet."
                body="The AMTC team will arrange this for you — see above."
              />
            ) : filtered.length === 0 ? (
              <EmptyResults
                hasFilters={selection.row1.length > 0 || search.trim().length > 0}
                onClear={() => {
                  setSearch('')
                  setParams(new URLSearchParams())
                }}
              />
            ) : mapView ? (
              <MapPlaceholder
                hospitals={filtered}
                // THE SPECIALTY TRAVELS (M33-T04). Without it the profile cannot
                // know why the patient is there, so it cannot open the right tab,
                // filter the right doctors or offer the right booking (T05).
                onSelect={(slug) => navigate(hospitalPath(slug, carried))}
                className="min-h-80 flex-1"
              />
            ) : (
              // The frame draws results as a card grid, three across on desktop
              // (screens-figma-results.jsx:208, gap --s-6). Column count comes from
              // the card's readable width, not the breakpoint name: the card
              // carries an 88px thumbnail plus a name, a distance and two badge
              // rows, which needs ~360px before the badges wrap. Measured: three
              // columns at `lg` give only 288px each, so three start at `xl`
              // (427px at 1440) and `lg` takes two (452px at 1024). Both clear
              // 360px; `lg`-at-three did not.
              <div className="grid gap-2 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3">
                {filtered.map((h) => (
                  <HospitalRow key={h.id} hospital={h} specialty={carried} />
                ))}
              </div>
            )}
          </div>
        </section>
      </PageContent>
    </PageShell>
  )
}

/**
 * The doctors for a symptom search, ahead of the hospitals (M33-T04b).
 *
 * Each card names the doctor and their specialty, and opens the doctor. The
 * hospital they practise at stays on the card as context AND as its own link,
 * carrying the specialty to the profile the way a hospital card does (T04) --
 * a link beside the row rather than inside it, because a button cannot hold
 * another control.
 *
 * Not in `designs/`: no frame shows doctors on the hospitals screen. The row is
 * the specialist page's own `SpecialistRow`, so the two read as one family.
 */
function DoctorsFirst({
  query,
  specialty,
  onOpen,
}: {
  query: UseQueryResult<SpecialistSearchResult>
  specialty: string
  onOpen: (specialistSlug: string) => void
}): ReactNode {
  if (query.isError) {
    return (
      <div role="alert" className="flex items-center gap-3 pb-3">
        <p className="text-[13px] text-ink-3">
          We couldn&apos;t load the doctors just now.
        </p>
        <Button kind="secondary" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    )
  }
  if (query.isPending) {
    return (
      <p role="status" aria-live="polite" className="pb-3 text-[13px] text-ink-3">
        Finding doctors…
      </p>
    )
  }
  const list = query.data?.data ?? []
  // Nobody to name: the hospitals below are the answer, as before.
  if (list.length === 0) return null
  return (
    <section aria-labelledby="results-doctors" className="pb-4">
      <h2
        id="results-doctors"
        className="pb-1.5 text-sm font-semibold text-ink-1"
      >
        Doctors
      </h2>
      <ul className="grid gap-2 lg:grid-cols-2 lg:gap-4">
        {list.map((s) => {
          const primary = s.hospitals.find((h) => h.is_primary) ?? s.hospitals[0]
          return (
            <li key={s.id} className="flex flex-col gap-1">
              <SpecialistRow specialist={s} onOpen={onOpen} />
              {primary && (
                <Link
                  to={hospitalPath(primary.slug, specialty)}
                  className="inline-flex items-center gap-1 self-end px-1 text-[12px] font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  {primary.name} <Icon name="arrow" size={12} />
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function NearMeButton({
  status,
  located,
  onClick,
}: {
  status: GeoStatus
  located: boolean
  onClick: () => void
}): ReactNode {
  const prompting = status === 'prompting'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={prompting}
      aria-label="Sort by hospitals near me"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Icon name="pin" size={14} />
      {prompting ? 'Locating…' : located ? 'Near me ✓' : 'Near me'}
    </button>
  )
}

/** Non-blocking note when location is denied/unavailable — location never gates
 * the list (M3 acceptance criteria). */
function GeoNote({ status }: { status: GeoStatus }): ReactNode {
  if (status !== 'denied' && status !== 'unavailable') return null
  const msg =
    status === 'denied'
      ? 'Location off — showing all hospitals. Enable location to sort by nearest.'
      : "Location isn't available on this device — showing all hospitals."
  return (
    <p className="pt-1.5 text-[11px] text-ink-3" role="status">
      {msg}
    </p>
  )
}

function ViewSwitch({
  mapView,
  onChange,
}: {
  mapView: boolean
  onChange: (map: boolean) => void
}): ReactNode {
  return (
    <div
      role="group"
      aria-label="List or map view"
      className="inline-flex gap-0.5 rounded-full border border-line-1 bg-bg-sunken p-[3px] text-[11px] font-medium"
    >
      {(
        [
          ['List', false],
          ['Map', true],
        ] as const
      ).map(([label, isMap]) => (
        <button
          key={label}
          type="button"
          aria-pressed={mapView === isMap}
          onClick={() => onChange(isMap)}
          className={
            mapView === isMap
              ? 'rounded-full bg-bg-app px-2.5 py-1 text-ink-1 shadow-sm'
              : 'rounded-full px-2.5 py-1 text-ink-3 hover:text-ink-1'
          }
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function ResultCount({
  query,
  count,
}: {
  query: { isPending: boolean; isError: boolean }
  count: number
}): ReactNode {
  let label: string
  if (query.isPending) label = 'Loading…'
  else if (query.isError) label = "Couldn't load hospitals"
  else label = `${count} ${count === 1 ? 'hospital' : 'hospitals'}`
  return (
    <p className="text-xs text-ink-3" aria-live="polite">
      {label}
    </p>
  )
}

function HospitalRow({
  hospital,
  specialty,
}: {
  hospital: HospitalListItem
  /** Carried into the profile so it knows why the patient is here. */
  specialty: string | null
}): ReactNode {
  const navigate = useNavigate()
  const distanceProp =
    hospital.distance_km != null
      ? { distance: `${hospital.distance_km} km` }
      : {}
  return (
    <div>
      <HospitalCard
        hospitalId={hospital.id}
        slug={hospital.slug}
        name={hospital.name}
        area={hospital.city}
        verified={hospital.verified}
        beds={bedBadges(hospital.beds)}
        services={serviceTags(hospital.services)}
        onSelect={() => navigate(hospitalPath(hospital.slug, specialty))}
        {...distanceProp}
      />
      <div className="flex justify-end px-1 pt-1">
        <button
          type="button"
          onClick={() => navigate(hospitalPath(hospital.slug, specialty))}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          See more <Icon name="arrow" size={12} />
        </button>
      </div>
    </div>
  )
}

/**
 * Skeleton placeholders while the list loads — no silent spinner.
 *
 * M17-T08 — the row and media radii were hardcoded `rounded-[10px]` and
 * `rounded-[8px]`, correct while `--radius-r3` was 10px. T03 moved that token
 * to 16px, so the skeleton stopped matching the card it stands in for and the
 * corners visibly changed shape the moment the list loaded. Tokens now, not
 * new literals: the whole point is that the next radius change reaches here.
 */
function ListSkeleton(): ReactNode {
  return (
    <div
      aria-hidden="true"
      className="grid gap-2 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3"
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[88px_1fr] gap-3 rounded-r3 border border-line-1 p-3"
        >
          <div className="h-[88px] animate-pulse rounded-r3 bg-bg-sunken" />
          <div className="flex flex-col gap-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-bg-sunken" />
            <div className="h-2.5 w-2/5 animate-pulse rounded bg-bg-sunken" />
            <div className="flex gap-1.5">
              <div className="h-[18px] w-16 animate-pulse rounded-full bg-bg-sunken" />
              <div className="h-[18px] w-20 animate-pulse rounded-full bg-bg-sunken" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * What the search understood, shown to the patient (M22-T09).
 *
 * Rendered ONLY when the server inferred — a literal match returns no
 * interpretation at all, so this never appears for "Britannia".
 *
 * It exists because inference without disclosure is the worst of both: a
 * patient handed dermatologists for "skin doctor" cannot tell whether we
 * understood them or got lucky, and a patient handed an EMPTY page for "nerve
 * problem" cannot tell that we understood perfectly and simply have nobody.
 */
/** Page-level wrapper: decides the copy and whether a clear action applies. */
function EmptyResults({
  hasFilters,
  onClear,
}: {
  hasFilters: boolean
  onClear: () => void
}): ReactNode {
  // M17-T05 proof site #1: title + body + action. Copy is unchanged — only the
  // shell moves to the shared component.
  return (
    <EmptyState
      title="No hospitals match"
      body={
        hasFilters
          ? 'Try clearing a filter or widening your search.'
          : 'No hospitals are available right now.'
      }
      action={
        hasFilters ? (
          <Button kind="secondary" onClick={onClear}>
            Clear filters
          </Button>
        ) : undefined
      }
    />
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-1 px-5 py-12 text-center"
    >
      <h2 className="text-base font-semibold text-ink-1">
        Couldn&apos;t load hospitals
      </h2>
      <p className="text-[13px] text-ink-3">
        Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Retry
      </button>
    </div>
  )
}
